<template>
  <div
    v-if="groups && groups.length > 1"
    :style="zIndexStyle"
    class="p-header-group-navi"
  >
    <div class="p-header-group-navi__header">
      <button
        v-touch:tap
        class="p-header-group-navi__heading"
        :class="buttonClass"
        @click="switchShownState"
        @click.stop
      >
        <span
          :style="{ color: textColor }"
          class="p-header-group-navi__heading__text"
        >
          {{ truncate(groupLabel, trimTextLength) }}</span
        >
        <i class="p-header-group-navi__heading__icon">
          <IconBase
            :width="24"
            :height="24"
            view-box="0 0 24 24"
            :icon-color="iconColor"
          >
            <IconArrowBottom />
          </IconBase>
        </i>
      </button>
    </div>
    <transition name="fade">
      <ul
        v-show="shown"
        class="p-header-group-navi__list c-common-rounded-shadow"
      >
        <li
          v-for="({ id, name }, key) in groups"
          :key="key"
          class="p-header-group-navi__list__item"
          @click.prevent.stop="updateGroupId(id, name)"
        >
          <input
            :id="`${idPrefix}-${id}`"
            type="radio"
            class="p-header-group-navi__input"
            :value="id"
          />
          <label
            v-touch:tap
            :for="`${idPrefix}-${id}`"
            class="p-header-group-navi__label c-common-rounded-shadow__label"
            >{{ name }}</label
          >
        </li>
      </ul>
    </transition>
  </div>
</template>

<script lang="ts">
import IconBase from "@/common/components/atoms/IconBase.vue";
import IconArrowBottom from "@/common/components/icons/IconArrowBottom.vue";
import { Options, Vue } from "vue-class-component";
import commons from "@/common/store/modules/commons";
import modalState from "@/common/store/modules/modalState";

import { getUserRoles } from "@/admin/store/api";
import LocalStorage from "@/general/utils/storage";

@Options({
  components: {
    IconBase,
    IconArrowBottom
  },
  watch: {
    closeModalState() {
      this.shown = false;
    }
  }
})
export default class HeaderGroupNavi extends Vue {
  iconColor = "#000000";
  textColor = "#000000";
  trimTextLength = 20;
  idPrefix = "groups";
  buttonWidth = "auto";

  value!: any;

  shown = false;
  switchState = false;

  groups: Array<any> = [];
  groupId = 0;
  groupLabel = "";
  headerInfo = commons.headerInfo;

  get zIndexStyle() {
    const { shown } = this;
    const zIndex = shown ? 2 : 0;

    return { zIndex };
  }

  get buttonClass() {
    const { buttonWidth } = this;
    const classNameBase = "p-header-group-navi__heading";
    const resultIndex = buttonWidth === "auto" ? "auto" : "other";
    const resultClassName = {
      auto: [`${classNameBase}--group`],
      other: [`${classNameBase}--search`]
    };

    return resultClassName[resultIndex];
  }

  get closeModalState() {
    return modalState.closeModalState;
  }

  setGroupLabel() {
    for (let i = 0; i < this.groups.length; i++) {
      if (this.groups[i].id === this.groupId) {
        this.groupLabel = this.groups[i].name;
        break;
      }
    }
  }

  async updateGroupId(id: number, name = "") {
    this.switchShownState();

    if (id !== this.groupId) {
      window.sessionStorage.setItem("selectedGroupId", id + "");
      window.sessionStorage.setItem("isGroupSelected", 1 + "");
      window.sessionStorage.setItem("selectedGroupName", name);
      window.sessionStorage.setItem("groupChangeLevel", 1 + "");

      LocalStorage.removeItem("USER_GROUP_ROLE_PERMISSIONS");
      LocalStorage.setItem("USER_GROUP_ID", id + "");

      const roles: any = await getUserRoles();
      LocalStorage.setItem("USER_ROLES", JSON.stringify(roles));

      this.groupId = id;
      this.setGroupLabel();

      this.$nextTick(() => {
        window.location.reload();
      });
    }
  }

  async created() {
    this.groups = JSON.parse(LocalStorage.getItem("USER_GROUPS") as string);

    this.groupId = parseInt(
      LocalStorage.getItem("USER_GROUP_ID") as string,
      10
    );

    this.setGroupLabel();

    if (this.groupLabel === "") {
      this.groupLabel = this.groups[0].group_name;
    }
  }

  switchShownState() {
    this.shown = !this.shown;
  }

  truncate(value: any, length = 20, omission = "...") {
    if (!value || value.length <= length) {
      return value;
    }
    return value.substring(0, length) + omission;
  }
}
</script>

<style lang="scss" scoped>
.p-header-group-navi {
  position: relative;

  &__header {
    position: relative;
  }

  &__input {
    width: 100%;
    height: 100%;
    display: block;
    position: absolute;
    left: 0;
    top: 0;
  }

  &__heading {
    position: relative;
    display: flex;
    align-items: center;

    &.tap {
      cursor: pointer;
    }

    &--search {
      width: 100%;
      justify-content: space-between;
      height: rem(48);
      padding: 0 rem(4);
    }

    &--group {
      padding: rem(12);
      font-weight: bold;
      @include large-sp {
        width: 100%;
      }
    }

    &__text {
      font-size: rem(14);
      @include large-sp {
        flex-grow: 1;
        text-align: left;
      }
    }

    &__icon {
      margin: 0 0 0 rem(8);
      display: flex;
      align-items: center;
    }
  }

  &__list {
    @include large-sp {
      top: rem(40);
      left: 0.75rem;
      width: 216px;
    }
    @include large-pc {
      top: rem(44);
    }
    width: rem(172);
    max-height: 75vh;
    overflow: auto;
    position: absolute;
    left: 0;
    padding: rem(14) 0;
    z-index: 1;

    &__item {
      position: relative;

      &:hover {
        background: rgba(240, 240, 240, 0.43);
      }
    }
  }

  &__label {
    font-size: rem(14);
    font-weight: bold;
    line-height: 1.4;
    padding: rem(8) rem(8);
    display: block;
    position: relative;
  }
}
</style>
